import { useEffect, useRef, useState } from 'react'
import { Payment } from '@yuno/sdk-web-core/dist/types'
import {
  getPaymentMethodsQuery,
  useCreatePayment,
  useLoadSdkYuno,
} from '../../../../hooks'
import { queryClient } from '../../../../state'
import { CheckoutInfo, CreatePaymentParams } from '../../../../types'
import { UsePaymentMethods } from './payment-methods-desktop.types'
import {
  getCustomButtonStyles,
  getPaymentMethods,
  hasCardForm,
} from '../payment-methods.utils'
import { dispatchLoaderEvent } from '../../../../utils/loader'
import { useTranslation } from 'react-i18next'
import {
  setPublicApiKeyInLS,
  setCheckoutReturnUrlInLS,
} from '../../../../utils/local-storage'
import { redirectTo } from '../../../../utils'

export const usePaymentMethodsDesktop: UsePaymentMethods = () => {
  const { isYunoLoaded } = useLoadSdkYuno()
  const getPaymentData = useRef<(oneTimeToken: string) => CreatePaymentParams>()
  const [accordionSelected, setAccordionSelected] = useState<
    string | undefined
  >()
  const checkoutInfo = queryClient.getQueryData<CheckoutInfo>(['checkout-info'])
  const { mutateAsync: createPayment } = useCreatePayment()
  const paymentMethods = queryClient.getQueryData<Payment.Method[]>(
    getPaymentMethodsQuery(
      checkoutInfo!.checkout_session,
      checkoutInfo!.merchant_public_api_key,
    ).queryKey,
  )
  const { t, i18n } = useTranslation()

  useEffect(() => {
    if (isYunoLoaded) {
      if (hasCardForm(checkoutInfo!, paymentMethods || [])) {
        setAccordionSelected(Payment.Type['CARD'])
        initSdk(Payment.Type['CARD'])
      } else {
        dispatchLoaderEvent({ showLoader: false })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isYunoLoaded])

  const initSdk = async (paymentMethodType: Payment.Type) => {
    const yuno = await Yuno.initialize(checkoutInfo!.merchant_public_api_key)

    await yuno.startCheckout({
      checkoutSession: checkoutInfo!.checkout_session,
      elementSelector: `#root-${paymentMethodType}`,
      countryCode: checkoutInfo!.payment_link.country,
      language: i18n.resolvedLanguage! as Language,
      showLoading: false,
      renderMode: {
        type: 'element',
        elementSelector: `#root-content-${paymentMethodType}`,
      },
      externalPaymentButtons: {
        paypal: {
          elementSelector: '#paypal',
        },
      },
      card: {
        vaultOnSuccess: checkoutInfo?.payment_link.vault_on_success,
        styles: `
          #root {
            .Yuno-card-form__title {
              display: none;
            }
  
            .Yuno-card-icon-bar__content {
              text-align: left;
            }

            .Yuno-card-icon-bar__icon {
              margin-right: 4px;
            }

            .Yuno-card-form__text-security {
              display: none;
            }

            .Yuno-card-form__submit {
              margin: 0;

              button {
                padding: 8px 16px;
                border-radius: 8px;
              }

              ${getCustomButtonStyles(checkoutInfo!.payment_link.style)}
            }
          }
        `,
        texts: {
          cardForm: {
            paymentSubmitButton: t('Pay'),
          },
        },
      },
      async yunoCreatePayment(oneTimeToken: string) {
        if (!getPaymentData.current) {
          throw new Error('Error getting payment function')
        }

        try {
          await createPayment(getPaymentData.current(oneTimeToken))
          setPublicApiKeyInLS(checkoutInfo!.merchant_public_api_key)
          setCheckoutReturnUrlInLS(checkoutInfo?.checkout_return_url)
          await yuno.continuePayment()
          dispatchLoaderEvent({ showLoader: false })
        } catch (_e) {
          dispatchLoaderEvent({ showLoader: false })
        }
      },
      yunoError: () => {
        yuno.continuePayment()
      },
      onLoading: ({ isLoading, type }) => {
        if (type === 'DOCUMENT') {
          dispatchLoaderEvent({ showLoader: isLoading })
        }
        if (type === 'ONE_TIME_TOKEN' && isLoading) {
          dispatchLoaderEvent({ showLoader: true })
        }
      },
      yunoPaymentResult: () => {
        if (checkoutInfo!.checkout_return_url) {
          dispatchLoaderEvent({ showLoader: true })
          redirectTo(checkoutInfo!.checkout_return_url)
        }
      },
    })

    yuno.mountCheckoutLite({
      paymentMethodType,
    })
  }

  getPaymentData.current = (oneTimeToken: string) => {
    return {
      payment_link: checkoutInfo!.payment_link.code,
      checkout: {
        session: checkoutInfo!.checkout_session,
      },
      payment_method: {
        token: oneTimeToken,
      },
      public_api_key: checkoutInfo!.merchant_public_api_key,
      account_code: checkoutInfo!.account_code,
      organization_code: checkoutInfo!.organization_code,
      customer_payer: {
        country: checkoutInfo!.country,
        browser_info: {
          user_agent: window.navigator.userAgent,
          color_depth: window.screen.colorDepth.toString(),
          screen_height: window.screen.height.toString(),
          screen_width: window.screen.width.toString(),
          javascript_enabled: true,
          language: i18n.resolvedLanguage || i18n.language,
        },
      },
    }
  }

  const onChangeAccordion = (openAccordion?: string) => {
    if (openAccordion) {
      const paymentMethodType = openAccordion.replace(
        'content-',
        '',
      ) as Payment.Type

      initSdk(paymentMethodType)
      setAccordionSelected(paymentMethodType)
    }
  }

  return {
    paymentMethods: getPaymentMethods(
      checkoutInfo!.payment_link.payment_method_types,
      paymentMethods,
    ),
    onChangeAccordion,
    accordionSelected,
  }
}
